import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useSearchCombined } from '@/common/apis/home/search-combined';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import debounceFunc from '@/common/utils/debounce.utility';

export function useAppbarSearch() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryText = searchParams.get('queryText') || '';
  const [value, setValue] = useState<string>(queryText);
  const {
    mutateAsync: searchCombinedMutate,
    data: searchResults,
    isPending: isLoading,
    reset: resetSearchResults,
  } = useSearchCombined();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSuggestPending, setIsSuggestPending] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    setValue(queryText);
  }, [queryText]);

  const handleSearch = useMemo(() => {
    const runSearch = debounceFunc(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setIsSuggestPending(false);
        resetSearchResults();
        return;
      }
      try {
        await searchCombinedMutate({
          queryText: searchQuery,
          categoryTake: 20,
          suggestTake: 20,
          vendorTake: 20,
        });
        setIsOpen(true);
      } finally {
        setIsSuggestPending(false);
      }
    }, 300);

    return (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setIsSuggestPending(false);
        resetSearchResults();
        runSearch(searchQuery);
        return;
      }
      setIsSuggestPending(true);
      runSearch(searchQuery);
    };
  }, [searchCombinedMutate, resetSearchResults]);

  const handleClearSearch = () => {
    setValue('');
    setIsOpen(false);
    setIsSuggestPending(false);
    resetSearchResults();
    if (pathname === '/search' || pathname.endsWith('/search')) {
      return;
    }
    if (pathname.includes('/search/')) {
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.delete('queryText');
    const nextQuery = params.toString();
    router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname);
  };

  const beginNavigation = () => {
    setIsNavigating(true);
  };

  const goToSearchPage = (nextQueryText: string) => {
    const trimmed = nextQueryText.trim();
    if (!trimmed) return;
    setIsNavigating(true);
    router.push(
      `/search/products${createQueryStringUtility({ queryText: trimmed })}`
    );
  };

  useEffect(() => {
    const overflow = 'overflow-hidden';
    const body = document.querySelector('body');
    if (isOpen || isNavigating) {
      body?.classList.add(overflow);
    } else {
      body?.classList.remove(overflow);
    }
    return () => {
      body?.classList.remove(overflow);
    };
  }, [isOpen, isNavigating]);

  const isSearching = isLoading || isSuggestPending;

  return {
    goToSearchPage,
    beginNavigation,
    handleSearch,
    searchResults,
    isLoading: isSearching,
    isNavigating,
    isOpen,
    setIsOpen,
    handleClearSearch,
    resetSearchResults,
    value,
    setValue,
  };
}
